import { AsyncButton, loadWrapper } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { IRequestStatus } from "functions/api/interface";
import { NotificationViewDto } from "functions/api/notifications/getUserNotifications";
import { updateAllNotificationsAsViewed } from "functions/api/notifications/updateAllNotificationsAsViewed";
import { Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from "react";
import { Check2All } from "react-bootstrap-icons";

interface NotificationsReadButtonProps {
  notificationsList: NotificationViewDto[];
  setNotificationsList: Dispatch<SetStateAction<NotificationViewDto[]>>;
}

/**
 * Button to mark all notifications as read.
 *
 * @param props - Destructured parameter object
 * @param props.notificationsList - Array of current notifications
 * @param props.setNotificationsList - Callback to update the notifications list
 */
export const NotificationsReadButton = ({
  notificationsList,
  setNotificationsList,
}: NotificationsReadButtonProps) => {
  const appSettings = useContext(AppSettingsContext);
  const hasUnreadNotifications = useMemo(() => {
    return notificationsList.some((n) => !n.notificationViewed);
  }, [notificationsList]);

  const [updateNotificationsStatusRequest, setUpdateNotifcationsStatusRequest] = useState<
    IRequestStatus<string>
  >({
    requesting: false,
    error: false,
  });

  const markAllNotificationsAsViewed = useCallback(async () => {
    const response = await loadWrapper(
      updateAllNotificationsAsViewed,
      "UpdateAll",
      { ...updateNotificationsStatusRequest, requestedId: undefined },
      setUpdateNotifcationsStatusRequest,
      appSettings.backEndUrl,
    );

    if (response?.success) {
      setNotificationsList(
        notificationsList.map((notification) => ({
          ...notification,
          notificationViewed:
            response.data.find((n) => n.notificationId === notification.notificationId)
              ?.notificationViewed ?? notification.notificationViewed,
        })),
      );
    }
  }, [
    appSettings.backEndUrl,
    notificationsList,
    setNotificationsList,
    updateNotificationsStatusRequest,
  ]);

  return (
    <AsyncButton
      id="mark-all-notifications-button"
      disabled={!hasUnreadNotifications}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        markAllNotificationsAsViewed();
      }}
    >
      <Check2All className="justify-self-center me-2" />
      Mark all as read
    </AsyncButton>
  );
};

NotificationsReadButton.displayName = "NotificationsReadButton";
